// GRWM's form (spec G14-grwm, PR D): a form with no live equivalent (live's GRWM is a Coming soon
// card, GRWM-13), built on the staged kit from the playbook's Fill in (L9384-9394) as Pixar
// Talking Ad's two approvals. The script stage picks the product, keeps the woman and the dress
// locked (each chip the playbook's approved one until changed; cleared, the approved one again),
// writes the ten lines (the server's director, the playbook's example, or the member) and
// approves them; Cast & set shows the sixteen stills (`GrwmStills.tsx`). The draft is the
// server's (`grwm-draft.ts`); "Generate video" starts the run it gives (GRWM-18).
import { Button, Group, Loader, Stack, Text, TextInput, Tooltip, UnstyledButton } from "@mantine/core";
import { Footprints, Monitor, Smartphone } from "lucide-react";
import { useState, type ReactNode } from "react";

import type { GrwmInputsResolution, GrwmScript, Job } from "../../../api/generated/model";
import {
  VideoTemplateQuote,
  VideoTemplateRequest,
  grwmInputsDressMax,
  grwmInputsDressShortMax,
  grwmInputsFaceLockMax,
  grwmInputsHairStyledMax,
  grwmInputsWomanMax,
  grwmScriptProductActionMax,
  type GrwmInputsOutput,
} from "../../../api/generated/zod";
import { ErrorAlert } from "../../../platform/ErrorAlert";
import { usePricedStart } from "../../../platform/jobs/priced-start";
import { Choice } from "../../../ui/Choice";
import { GlassPanel } from "../../../ui/GlassPanel";
import { ReuseSwitch, type TemplateFormProps } from "./kit";
import { InputChip, ScriptSection } from "./PodcastScript";
import { GrwmStillsStage } from "./GrwmStills";
import { Submit } from "./staged";
import { StreetProductTile, type ProductWords } from "./StreetTiles";
import { useGrwmDraft, videoBody, type DraftWork, type VideoSettings } from "./grwm-draft";
import {
  BEAT_NAMES,
  GENERATE_SCRIPT,
  MAX_LINE_WORDS,
  NAME_THE_PRODUCT,
  PICK_PRODUCT,
  STILLS_PENDING,
  blankScript,
  exampleToFinish,
  scriptIssue,
  scriptMeta,
} from "./grwm-script";
import { sameLineTexts } from "./street-script";
import classes from "./podcast.module.css";

/** Our words (no live equivalent). */
const PRODUCT_WORDS: ProductWords = {
  tile:
    "The product and the exact photo of it the ad is built on. Pick one of your products and its " +
    "packshot, or upload a packshot — she picks it up in the entryway and it is on every later image.",
  name: "The product name her entryway line says. An upload needs it: nothing reads it off the packshot.",
  text: "A description or the product page, for the director's script. Leave empty and the director works from the name and the picture.",
};
const SCRIPT_TOOLTIP =
  "Ten lines of her voiceover, one a 3-second beat, from the shower to the toast: about 70 words. " +
  "Line 6, the entryway, names your product — edit any line before approving.";
const ACTION_TOOLTIP = "What she does with your product in the entryway beat, e.g. putting in her hoops.";
const STAIRS_TOOLTIP =
  "Re-shoot the stoop beat chest-up and lay it over the clip, so her legs never show: up to three " +
  "4-second takes, each checked. Off, clip two's own stoop beat stays.";
const RESOLUTION_TOOLTIP = "Resolution of every clip. 720p costs about 40% of 1080p.";
const FORMAT_TOOLTIP = "Every image and clip is vertical 9:16.";
const USE_EXAMPLE = "Use the example script";
const WRITE_SCRIPT = "Write the 10 lines yourself";
const SCRIPT_HINT = { wand: "to write the 10 lines from the product, or", pencil: "to write them yourself." };
const APPROVE_SCRIPT = "Approve script";
const GENERATE_VIDEO = "Generate video";
const HANDOFF_NOTICE = "Your GRWM ad is finishing in My Generations — you can close this panel now.";
const RESOLUTIONS: readonly GrwmInputsResolution[] = ["1080p", "720p"];
/** The locks, each the playbook's approved fill-in until changed (L9384-9394). */
const LOCKS = [
  { field: "woman", label: "Woman", tooltip: "Who she is, as every image draws her.", maxLength: grwmInputsWomanMax },
  { field: "face_lock", label: "Face", tooltip: "Her face, kept the same in every image.", maxLength: grwmInputsFaceLockMax },
  { field: "dress", label: "Dress", tooltip: "The dress she reveals, as every image after it shows it.", maxLength: grwmInputsDressMax },
  { field: "dress_short", label: "Dress, short", tooltip: "The dress in a few words, as the clips name it.", maxLength: grwmInputsDressShortMax },
  { field: "hair_styled", label: "Hair", tooltip: "Her hair once styled, dry, from the dress reveal on.", maxLength: grwmInputsHairStyledMax },
] as const;

function progressOf(work: DraftWork, drawn: number, total: number): string | null {
  if (work.writing) return "Writing the script";
  return work.drawing ? `Generating images · ${drawn}/${total}` : null;
}

/**
 * The form: the stage, and in the footer the stairs re-shoot, the resolution, the 9:16 format and
 * the round start, "Approve script" then "Generate video", whose tooltip says what it waits for.
 * The stairs switch and the resolution stay editable after Approve script: neither changes a
 * still.
 */
export function GrwmForm(props: TemplateFormProps) {
  const { teamId, brandId, above, onStarted, example } = props;
  const [collapsed, setCollapsed] = useState(false);
  const flow = useGrwmDraft(teamId, brandId);
  const [baseline, setBaseline] = useState<GrwmScript | null | undefined>(undefined);
  const [submitted, setSubmitted] = useState(false);
  const [wasWriting, setWasWriting] = useState(false);
  const [productAsked, setProductAsked] = useState(false);
  const [settings, setSettings] = useState<VideoSettings | null>(null);
  // The run started here: the hand-off shows once it starts.
  const [started, setStarted] = useState(false);
  const { form, draft } = flow;
  const writing = flow.work.writing;
  const approved = draft !== undefined && draft.stage !== "script";
  const shownSettings: VideoSettings | null =
    form === null
      ? null
      : ((approved ? settings : null) ?? { resolution: form.inputs.resolution, stairs: form.inputs.stairs });
  const video = draft === undefined || shownSettings === null ? null : videoBody(draft, shownSettings);
  const videoRequest = VideoTemplateRequest.safeParse(video);
  const videoQuote = VideoTemplateQuote.safeParse(video);
  const videoStart = usePricedStart({
    teamId,
    brandId,
    quote: videoQuote.success ? videoQuote.data : undefined,
    request: videoRequest.success ? videoRequest.data : undefined,
    onStarted: (job: Job) => {
      setStarted(true);
      flow.refresh();
      onStarted(job);
    },
  });
  if (!approved && settings !== null) setSettings(null);
  if (!approved && started) setStarted(false);
  if (draft !== undefined && baseline === undefined) setBaseline(draft.script);
  if (writing !== wasWriting) {
    setWasWriting(writing);
    if (!writing && draft !== undefined && draft.script_director !== "failed") setBaseline(draft.script);
  }

  const panel = (body: ReactNode, footer?: ReactNode) => (
    <GlassPanel
      collapsed={collapsed}
      onCollapsedChange={setCollapsed}
      above={above}
      header={<ReuseSwitch example={null} checked={false} onChange={() => undefined} />}
      summary={<Text size="sm">GRWM</Text>}
      footer={footer}
    >
      {body}
    </GlassPanel>
  );
  if (flow.read.isError) return panel(<ErrorAlert error={flow.read.error} />);
  if (draft === undefined || form === null || shownSettings === null) {
    return panel(<Loader size="sm" aria-label="Loading the draft" />);
  }

  const { inputs, script } = form;
  const setInputs = (change: Partial<GrwmInputsOutput>) => flow.editInputs(change);
  const changeSettings = (change: Partial<VideoSettings>) => {
    if (approved) setSettings({ ...shownSettings, ...change });
    else setInputs(change);
  };
  const setScript = (next: GrwmScript) => flow.edit({ script: next });
  const locked = !flow.editable;
  const drawn = draft.stills.filter((each) => each.status === "done").length;
  const progress = progressOf(flow.work, drawn, draft.stills.length);
  // An upload's name is the member's to type; a brand product has its own (GRWM-19).
  const nameMissing = inputs.product != null && inputs.product.kind !== "product" && inputs.product_name.trim() === "";
  const issue = scriptIssue(script);
  const edited = script !== null && !sameLineTexts(script, baseline ?? null);
  const shownIssue = script !== null && !writing && (edited || submitted) ? issue : null;

  let submit;
  if (approved) {
    const ready = video !== null;
    submit = (
      <Submit
        label={GENERATE_VIDEO}
        reasons={ready ? [] : [STILLS_PENDING]}
        disabled={!ready || flow.busy || !videoStart.canStart || videoStart.start.isPending || started}
        loading={videoStart.start.isPending}
        onClick={videoStart.startJob}
      />
    );
  } else {
    const reasons = [
      ...(inputs.product ? [] : [PICK_PRODUCT]),
      ...(nameMissing ? [NAME_THE_PRODUCT] : []),
      ...(script === null ? [GENERATE_SCRIPT] : []),
    ];
    submit = (
      <Submit
        label={APPROVE_SCRIPT}
        reasons={reasons}
        disabled={reasons.length > 0 || issue !== null || flow.busy}
        loading={flow.approving}
        onClick={() => {
          setSubmitted(true);
          flow.approve();
        }}
      />
    );
  }

  const footer = (
    <Group justify="space-between" wrap="nowrap" gap={12}>
      <div className={classes.settings}>
        <Tooltip label={STAIRS_TOOLTIP} multiline maw={280} withArrow>
          <UnstyledButton
            className={classes.setting}
            data-on={shownSettings.stairs || undefined}
            aria-pressed={shownSettings.stairs}
            disabled={flow.busy || started}
            onClick={() => changeSettings({ stairs: !shownSettings.stairs })}
          >
            <Footprints size={16} strokeWidth={1.5} aria-hidden />
            {shownSettings.stairs ? "Stairs re-shoot on" : "Stairs re-shoot off"}
          </UnstyledButton>
        </Tooltip>
        <Choice
          variant="footer"
          setting="Resolution"
          icon={<Monitor size={16} strokeWidth={1.5} />}
          tip={RESOLUTION_TOOLTIP}
          width={88}
          value={shownSettings.resolution}
          options={RESOLUTIONS.map((value) => ({ value, label: value }))}
          disabled={flow.busy || started}
          onChange={(next) => {
            const resolution = RESOLUTIONS.find((each) => each === next);
            if (resolution !== undefined) changeSettings({ resolution });
          }}
        />
        <Tooltip label={FORMAT_TOOLTIP} multiline maw={280} withArrow>
          <span className={classes.setting}>
            <Smartphone size={16} strokeWidth={1.5} aria-hidden />
            9:16
          </span>
        </Tooltip>
      </div>
      {submit}
    </Group>
  );

  const refused = videoStart.start.error ?? videoStart.quoteError ?? flow.failure;
  const status = (
    <>
      {draft.problem === null ? null : (
        <Text className={classes.problem} role="alert">
          {draft.problem}
        </Text>
      )}
      {refused === null ? null : <ErrorAlert error={refused} />}
      {progress === null ? null : (
        <output className={classes.progress}>
          <Loader size={14} aria-hidden />
          {progress}
        </output>
      )}
      {started && approved ? (
        <Text component="output" size="sm">
          Generation started. {HANDOFF_NOTICE}
        </Text>
      ) : null}
    </>
  );
  if (approved) {
    return panel(
      <div className={classes.content}>
        <GrwmStillsStage
          stills={draft.stills}
          busy={flow.busy}
          resetting={flow.resetting}
          onRegenerate={(name) => {
            // A new still allows a new run, and the hand-off line is about the last one.
            setStarted(false);
            flow.regenerate(name);
          }}
          onStartOver={flow.startOver}
        />
        {status}
      </div>,
      footer,
    );
  }

  const body = (
    <div className={classes.content}>
      <Stack gap={12}>
        <div className={classes.tiles}>
          <StreetProductTile
            teamId={teamId}
            brandId={brandId}
            pick={inputs.product ?? null}
            typed={{ product_name: inputs.product_name, product_text: inputs.product_text }}
            disabled={locked}
            missing={productAsked}
            exampleUrls={example?.urls ?? {}}
            words={PRODUCT_WORDS}
            onChange={(product, typed) => setInputs({ product, ...typed })}
          />
        </div>
        {productAsked && nameMissing ? <Text className={classes.fieldError}>{NAME_THE_PRODUCT}</Text> : null}
        <div className={classes.chips}>
          {LOCKS.map((lock) => (
            <InputChip
              key={lock.field}
              label={lock.label}
              value={inputs[lock.field]}
              placeholder={`${lock.label} (the approved one)`}
              tooltip={lock.tooltip}
              maxLength={lock.maxLength}
              disabled={locked}
              onChange={(value) => {
                const change: Partial<GrwmInputsOutput> = {};
                change[lock.field] = value;
                setInputs(change);
              }}
            />
          ))}
        </div>
        <ScriptSection
          lines={
            script === null
              ? null
              : script.lines.map((line, index) => ({ id: index + 1, speaker: null, beat: BEAT_NAMES[line.beat], text: line.text }))
          }
          meta={script === null ? null : scriptMeta(script.lines)}
          tooltip={SCRIPT_TOOLTIP}
          primary={null}
          maxWords={() => MAX_LINE_WORDS}
          hint={SCRIPT_HINT}
          writeLabel={WRITE_SCRIPT}
          writing={writing}
          locked={locked}
          issue={shownIssue}
          warning={draft.script_problems.length > 0 && script !== null ? draft.script_problems.join(" ") : null}
          onGenerate={() => {
            if (!inputs.product || nameMissing) {
              setProductAsked(true);
              return;
            }
            flow.generateScript();
          }}
          onWrite={() => {
            const blank = blankScript();
            setBaseline(blank);
            setScript(blank);
          }}
          onLine={(index, text) => {
            if (script === null) return;
            setScript({ ...script, lines: script.lines.map((line, at) => (at === index ? { ...line, text } : line)) });
          }}
        />
        {script === null ? null : (
          <Tooltip label={ACTION_TOOLTIP} multiline maw={280} withArrow>
            <TextInput
              label="Entryway action"
              value={script.product_action}
              maxLength={grwmScriptProductActionMax}
              disabled={locked}
              onChange={(event) => setScript({ ...script, product_action: event.currentTarget.value })}
            />
          </Tooltip>
        )}
        <Group>
          <Button
            variant="subtle"
            size="compact-sm"
            disabled={locked}
            onClick={() => {
              // The baseline is the whole example, so the blanks count as edited and Approve asks for them.
              setBaseline(draft.example);
              setScript(exampleToFinish(draft.example));
            }}
          >
            {USE_EXAMPLE}
          </Button>
        </Group>
      </Stack>
      {status}
    </div>
  );
  return panel(body, footer);
}
